import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import SessionTimeoutModal from '../components/SessionTimeoutModal';

const useIdleTimeout = (timeoutMinutes = 80) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showToast } = useToast();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const [showWarningModal, setShowWarningModal] = useState(false);

  const resetTimer = () => {
    lastActivityRef.current = Date.now();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    setShowWarningModal(false);
    // Set warning timeout (4 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarningModal(true);
    }, (timeoutMinutes - 1) * 60 * 1000);
    // Set final timeout (5 minutes)
    timeoutRef.current = setTimeout(handleIdle, timeoutMinutes * 60 * 1000);
  };

  const handleIdle = async () => {
    const currentTime = Date.now();
    const idleTime = currentTime - lastActivityRef.current;
    if (idleTime >= timeoutMinutes * 60 * 1000) {
      try {
        await logout();
        showToast('Session expired due to inactivity', 'warning');
        navigate('/login');
      } catch (error) {
        console.error('Error during idle logout:', error);
      }
    }
  };

  const handleStayLoggedIn = () => {
    resetTimer();
  };

  const handleLogout = async () => {
    try {
      await logout();
      showToast('You have been logged out', 'info');
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  useEffect(() => {
    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Initial timer setup
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [timeoutMinutes]);

  return {
    resetTimer,
    SessionTimeoutModal: (
      <SessionTimeoutModal
        open={showWarningModal}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogout}
      />
    ),
  };
};

export default useIdleTimeout; 